import React, { useState } from 'react';
import './Dashboard.css';

function Dashboard({ onTaskSelect }) {
  const [showVision, setShowVision] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const automationTasks = [
    {
      id: 'lmcu-refi',
      title: 'Residential Refinance Order',
      description: 'Upload residential refinance documents. Agentic Sunny extracts property and borrower information with multi-AI consensus, verifies data against DataTrace public records, pre-fills the order form with color-coded confidence indicators, and optionally creates the order in SoftPro.',
      icon: '🏠',
      status: 'active',
      features: [
        'Multi-AI consensus extraction',
        'DataTrace property verification',
        'Color-coded confidence indicators',
        'Optional SoftPro order creation'
      ]
    }
  ];

  const toolTasks = [
    {
      id: 'gemini-extract',
      title: 'Gemini Visual Extraction',
      description: 'Use Google Gemini\'s advanced visual AI to extract text and data from any document, including hard-to-read handwriting, scanned documents, and complex forms.',
      icon: '🔴',
      status: 'active',
      features: [
        'Industry-leading visual AI',
        'Handwriting recognition',
        'Scanned document processing',
        'Structured data extraction'
      ]
    },
    {
      id: 'file-renamer',
      title: 'AI Document Renamer',
      description: 'Automatically rename recorded documents for SoftPro ingestion. AI analyzes PDFs to extract Instrument Numbers, Document Types, and Recording Dates to generate proper filenames.',
      icon: '✏️',
      status: 'active',
      features: [
        'Bulk PDF processing',
        'AI-powered document analysis',
        'SoftPro naming conventions',
        'Instant file downloads'
      ]
    },
    {
      id: 'datatrace-tester',
      title: 'DataTrace API Tester',
      description: 'Test different DataTrace API products and endpoints. Experiment with property_data, tax, and other products. Configure product codes, types, and endpoints to see raw API responses.',
      icon: '🧪',
      status: 'active',
      features: [
        'Test multiple API products',
        'Configure endpoints & types',
        'View raw request/response',
        'Compare different APIs'
      ]
    },
    {
      id: 'titlepoint',
      title: 'TitlePoint API Tester',
      description: 'Complete TitlePoint SOAP API integration with automated workflow and manual testing. Submit property searches, poll for completion, view transaction logs, and test custom XML requests.',
      icon: '🔄',
      status: 'active',
      features: [
        'Automated workflow execution',
        'Manual API testing',
        'Transaction logging',
        'Custom XML support'
      ]
    }
  ];

  const legacyTasks = [
    {
      id: 'accelerate-integration',
      title: 'Stewart Accelerate Integration Mock-Up',
      description: 'Upload order PDFs or Accelerate report PDFs to auto-fill Stewart Accelerate order forms. Demonstrates how Sunny AI can complement Stewart Accelerate with intelligent data extraction and SoftPro integration.',
      icon: '⚡',
      status: 'active',
      features: [
        'PDF upload with AI auto-fill',
        'Order & Accelerate report support',
        'DataTrace property verification',
        'Create orders in SoftPro'
      ]
    },
    {
      id: 'single-pdf',
      title: 'Create Order from Purchase Agreement',
      description: 'Upload a purchase agreement PDF from your customer. Agentic Sunny will extract property and order information, get consensus from multiple AI providers, and pre-fill your title order form.',
      icon: '📄',
      status: 'active',
      externalUrl: process.env.REACT_APP_ANALYSIS_API_URL || 'https://sunny-analysis.messagesimproved.com/',
      features: [
        'Single PDF upload',
        'Multi-AI consensus analysis',
        'Auto-fill order fields',
        'Review and edit before submission'
      ]
    },
    {
      id: 'multi-source',
      title: 'Summarize Order from Multiple Sources',
      description: 'Drag and drop multiple files or folders, or copy and paste text from various sources. Agentic Sunny will analyze all inputs, get consensus from multiple AI providers, and create a comprehensive order summary for the examiner.',
      icon: '📁',
      status: 'coming',
      features: [
        'Multi-file and folder upload',
        'Text paste support',
        'Cross-document analysis',
        'Comprehensive order summary'
      ]
    },
    {
      id: 'datatrace',
      title: 'DataTrace Property Search',
      description: 'Search property information using DataTrace API. Enter an address to retrieve comprehensive property details, public records, tax information, and ownership history.',
      icon: '🏠',
      status: 'active',
      features: [
        'Address-based property lookup',
        'Tax & assessment data',
        'Ownership history',
        'Public records retrieval'
      ]
    },
    {
      id: 'property-lookup',
      title: 'Check Public Property Information',
      description: 'Query title vendor APIs to retrieve public records and information about a property. Get instant access to property history, liens, encumbrances, and other public data.',
      icon: '🔍',
      status: 'coming',
      features: [
        'Title vendor API integration',
        'Public records search',
        'Property history lookup',
        'Automated data retrieval'
      ]
    }
  ];

  const renderTaskCard = (task) => (
    <div
      key={task.id}
      className={`task-card ${task.status === 'coming' ? 'coming-soon' : ''}`}
      onClick={() => {
        if (task.status === 'active') {
          if (task.externalUrl) {
            window.open(task.externalUrl, '_blank');
          } else {
            onTaskSelect(task.id);
          }
        }
      }}
    >
      <div className="task-header">
        <div className="task-icon">{task.icon}</div>
        {task.status === 'coming' && (
          <span className="coming-badge">Coming Soon</span>
        )}
        {task.externalUrl && (
          <span className="external-badge">External</span>
        )}
      </div>

      <h3 className="task-title">{task.title}</h3>
      <p className="task-description">{task.description}</p>

      <ul className="task-features">
        {task.features.map((feature, index) => (
          <li key={index}>
            <span className="feature-bullet">•</span>
            {feature}
          </li>
        ))}
      </ul>

      {task.status === 'active' && (
        <button className="task-button">
          {task.externalUrl ? 'Open App ↗' : 'Get Started →'}
        </button>
      )}
    </div>
  );

  return (
    <div className="dashboard">
      <div className="dashboard-intro">
        <h2>Welcome to the Agentic Sunny Lab</h2>
        <p className="dashboard-subtitle">
          Alpha testing environment for AI-powered title tools
        </p>
        <p className="dashboard-description">
          This is our working laboratory where we assemble and showcase early versions of AI-powered tools for Sun Title employees. Test the latest automation workflows and utilities as we build the future of title examination. Your feedback shapes what comes next.
        </p>
      </div>

      <div className="section-container">
        <div className="section-header">
          <h3 className="section-title">Automation</h3>
          <p className="section-description">
            Agentic workflows that execute end-to-end processes with human oversight at critical decision points
          </p>
        </div>

        <div className="vision-section">
          <button
            className="vision-toggle-button"
            onClick={() => setShowVision(!showVision)}
          >
            {showVision ? '▼' : '▶'} Full Automation Vision
          </button>

          {showVision && (
            <div className="vision-content">
              <div className="vision-header">
                <h4>Sunny Agentic AI Overview</h4>
                <p className="vision-subtitle"><strong>Task:</strong> Order Entry & Title Exam Automation</p>
                <p className="vision-scope"><strong>Initial Scope:</strong> Residential Refinance in Kent & Ottawa Counties</p>
              </div>

              <div className="vision-synopsis">
                <p>
                  Online web portal that acts as a standalone middleware powered by Sunny as an agentic AI that
                  automates/streamlines the order process from initiation to commitment. By capturing order details
                  from online forms and emails, Sunny automates tax verification and title searches. Should any
                  discrepancies arise, a Sun Title employee will be prompted to provide a decision.
                </p>
              </div>

              <div className="vision-phases">
                <div className="vision-phase">
                  <h5>1. Order Entry</h5>
                  <p>
                    Order Entry information will be extracted by OCR and submitted to multiple AI models for analysis
                    to automatically extract the data and thereby eliminating the need for manual data entry. By having
                    a consensus of models reviewing the information, it ensures discrepancies are elevated and evaluated
                    by a human. The information will be cross-checked with the public records data from DataTrace to
                    verify that the property details are consistent with the order submitted. If all models agree, on a
                    per field basis, then the entry is prefilled in the Sunny portal. If even one model disagrees, the
                    human will be brought in to evaluate and select or override the field with the correct information.
                    Sunny will also look at the contacts on the order and add them to SoftPro. Once all fields required
                    for the order are completed, the employee will press a button that will submit the order for automatic
                    creation in SoftPro.
                  </p>
                </div>

                <div className="vision-phase">
                  <h5>2. Tax Verification</h5>
                  <p>
                    With the order now created in SoftPro the tax verification can be completed. Within the Sunny Portal
                    an order for tax verification will be submitted to DataTrace and the results will include all needed
                    information. If there are any specials or discrepancies, Sunny will provide a call out for further
                    investigation by the employee. Once the data has been verified, the employee will press a button that
                    will submit the information to SoftPro.
                  </p>
                </div>

                <div className="vision-phase">
                  <h5>3. Title Search</h5>
                  <p>
                    Check DataTrace for the recordable documents related to the property, push to SP, review documents to
                    see if the information in the document(s) needs to be included in the commitment. Will also search Sun
                    Title's historical information for priors and if there are any discrepancies, it will bring in the human
                    for evaluation. Once data is cleared, all documents will be pushed into SoftPro. Sunny will also capture
                    requirements and exceptions from the documents and apply the correct codes in SoftPro.
                  </p>
                </div>

                <div className="vision-phase">
                  <h5>4. Commitment Generation</h5>
                  <p>
                    With all information validated and entered in SoftPro, Sunny will generate the commitment and deliver
                    to the relevant parties on the order.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="tasks-grid">
          {automationTasks.map(renderTaskCard)}
        </div>
      </div>

      <div className="section-container">
        <div className="section-header">
          <h3 className="section-title">Tools</h3>
          <p className="section-description">
            Standalone AI utilities to enhance your daily workflow
          </p>
        </div>
        <div className="tasks-grid">
          {toolTasks.map(renderTaskCard)}
        </div>
      </div>

      <div className="section-container">
        <div className="section-header">
          <h3 className="section-title">Mockups & Legacy</h3>
          <p className="section-description">
            Demonstration mockups and earlier prototypes for reference
          </p>
        </div>

        <div className="vision-section">
          <button
            className="vision-toggle-button"
            onClick={() => setShowLegacy(!showLegacy)}
          >
            {showLegacy ? '▼' : '▶'} Show Mockups & Legacy
          </button>

          {showLegacy && (
            <div className="tasks-grid" style={{ marginTop: '20px' }}>
              {legacyTasks.map(renderTaskCard)}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-footer">
        <div className="info-grid">
          <div className="info-item">
            <div className="info-icon">🤖</div>
            <div>
              <h4>AI Consensus</h4>
              <p>OpenAI, Claude & Gemini work together for accurate data extraction</p>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">🔗</div>
            <div>
              <h4>API Integration</h4>
              <p>Connect to title vendor APIs for instant public records</p>
            </div>
          </div>
          <div className="info-item">
            <div className="info-icon">⚡</div>
            <div>
              <h4>Save Time</h4>
              <p>Reduce manual data entry and streamline order processing</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
